import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import useTracking from "@/lib/startrack/hooks/useTracking";
import { useMapStore } from "@/lib/stores/mapStore";
import { useReactiveSearchParams } from "@/lib/Hooks/useReactiveSearchParams";
import { getFreshClickId } from "@/lib/Helpers/getFreshClickId";
import { StartrackingEventsNames } from "@/lib/startrack/trackingEvents";
import { AppConfig } from "@/lib/Types/AppConfig";

type MapContext = "results" | "details";

const resolveContext = (pathname: string): MapContext | null => {
  if (pathname.includes("/hotel/")) return "details";
  if (pathname.includes("/liste")) return "results";
  return null;
};

export default function useMapClickTracking(appConfig: AppConfig) {
  const { track } = useTracking(appConfig);
  const pathname = usePathname();
  const showMap = useMapStore((state) => state.showMap);
  const searchParams = useReactiveSearchParams();
  const prevShowMap = useRef(showMap);

  useEffect(() => {
    const transitionedOpen = showMap && !prevShowMap.current;
    prevShowMap.current = showMap;
    if (!transitionedOpen) return;

    const context = resolveContext(pathname);
    if (context === null) return;

    const searchId = searchParams.get("searchId") ?? "";
    if (!searchId) return;
    track(StartrackingEventsNames.MapClicked, {
      searchId,
      clickId: getFreshClickId() || null,
      context,
    });
  }, [showMap, pathname, searchParams, track]);
}
